
import React from "react";
import { useSettings } from "@/context/SettingsContext";
import { cn } from "@/lib/utils";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { HelpCircle } from "lucide-react";

interface HowItWorksDialogProps {
  trigger?: React.ReactNode;
  hideDefaultTrigger?: boolean;
}

const HowItWorksDialog: React.FC<HowItWorksDialogProps> = ({ trigger, hideDefaultTrigger = false }) => {
  const { darkMode } = useSettings();
  
  return (
    <Dialog>
      <DialogTrigger asChild>
        {trigger || (
          !hideDefaultTrigger && (
            <Button
              variant="ghost"
              size="icon"
              className={cn(
                "absolute top-2 right-2 rounded-full",
                darkMode ? "text-[#FBF3F1]/70 hover:text-[#FBF3F1] hover:bg-[#6127A0]/20" : 
                          "text-gray-500 hover:text-[#6127A0] hover:bg-[#6127A0]/10"
              )}
            >
              <HelpCircle className="h-5 w-5" />
              <span className="sr-only">How it works</span>
            </Button>
          )
        )}
      </DialogTrigger>
      
      <DialogContent className={cn(
        "sm:max-w-[500px]",
        darkMode 
          ? "bg-[#2d1a45] text-[#FBF3F1] border-[#6127A0]/30" 
          : "bg-white text-gray-700 border-[#D896A4]/20"
      )}>
        <DialogHeader>
          <DialogTitle className={cn(
            "text-xl font-semibold",
            darkMode ? "text-[#00FF99]" : "text-[#6127A0]"
          )}>
            Stuck? Start here.
          </DialogTitle>
          <DialogDescription className={cn(
            "text-base leading-relaxed mt-2",
            darkMode ? "text-[#FBF3F1]/80" : "text-gray-600"
          )}>
            Sometimes you can't see what to do next — not because you don't know, but because the cues aren't clear.
          </DialogDescription>
        </DialogHeader>
        
        <div className="mt-4 space-y-4">
          <p className={cn(
            "text-base",
            darkMode ? "text-[#FBF3F1]" : "text-gray-700"
          )}>
            This tool helps you reconnect with what's already guiding you — through your body, your space, or your attention — so you can begin again, from where you are.
          </p>
          
          <div className="mt-6">
            <h3 className={cn(
              "text-lg font-medium flex items-center gap-2 mb-3",
              darkMode ? "text-[#00FF99]" : "text-[#6127A0]"
            )}>
              🎲 How It Works
            </h3>
            
            <ol className={cn(
              "list-decimal list-inside space-y-2 pl-1",
              darkMode ? "text-[#FBF3F1]" : "text-gray-700"
            )}>
              <li>Bring to mind what you're trying to do</li>
              <li>Roll the dice</li>
              <li>Notice what shows up</li>
              <li>If something feels meaningful — move toward it, even a little</li>
              <li>If not — roll again. You're tuning in, not trying harder.</li>
            </ol>
          </div>
          
          <div className="pt-4">
            <DialogClose asChild>
              <Button 
                className={cn(
                  "w-full",
                  darkMode 
                    ? "bg-[#6127A0] hover:bg-[#4d1f80] text-white" 
                    : "bg-[#6127A0] hover:bg-[#4d1f80] text-white"
                )}
              >
                Got it
              </Button>
            </DialogClose>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default HowItWorksDialog;
